﻿import React, { useMemo, useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Icon } from 'semantic-ui-react';
import { useGetAllTeamsQuery, useGetMyTeamsQuery, useDeleteTeamMutation, useArchiveTeamMutation, useUpdateTeamMutation } from '../api/teamApi';
import { Team } from '../types';
import { TeamEditModal } from './TeamEditModal';
import { TeamMembersModal } from './TeamMembersModal';
import { PositionedModal } from '../../../common/components/PositionedModal';
import { H2 } from '@/common/styles/page';
import { StyledArchiveButton, StyledCancelButton, StyledDeleteButton } from 'common/components/button/StyledButton';
import { TeamOrgNode, IndependentTeamCard } from './TeamOrgNode';
import { buildTree } from './TeamOrgStructureUtils';
import {
  OuterWrapper,
  Container,
  Chart,
  Column,
  ChartHeader,
  FilterGroup,
  FilterSelect,
  LayoutToggleGroup,
  LayoutToggleButton,
  IndependentSection,
  NodeMenu,
  NodeMenuItem,
  ScrollControls,
  ScrollButton,
  EmptyMessage,
} from './TeamOrgStructure.styles';

const MENU_WIDTH = 160;

interface Props {
  onTeamSelect: (team: Team) => void;
  onSelectedTeamChange?: (team: Team | null) => void;
  initialSelectedTeamId?: string;
}

const TeamOrgStructure: React.FC<Props> = ({ onSelectedTeamChange, initialSelectedTeamId }) => {
  const { data: allData, refetch: refetchAll } = useGetAllTeamsQuery();
  const { data: myData, refetch: refetchMy } = useGetMyTeamsQuery();
  const [deleteTeam, { isLoading: isDeleting }] = useDeleteTeamMutation();
  const [archiveTeam, { isLoading: isArchiving }] = useArchiveTeamMutation();
  const [updateTeam] = useUpdateTeamMutation();
  const isMutatingTeam = isDeleting || isArchiving;

  const teams = allData?.results?.length ? allData.results : myData?.results || [];
  const [selectedTeamId, setSelectedTeamId] = useState<string>(initialSelectedTeamId || '');

  React.useEffect(() => {
    setSelectedTeamId(initialSelectedTeamId || '');
  }, [initialSelectedTeamId]);

  const allTeamsSorted = useMemo(
    () => [...teams].sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  );

  const selectedTeam = useMemo(() => {
    const parsedTeamId = selectedTeamId ? Number(selectedTeamId) : null;
    if (!parsedTeamId || Number.isNaN(parsedTeamId)) {
      return null;
    }
    return teams.find(team => team.id === parsedTeamId) || null;
  }, [selectedTeamId, teams]);

  const { roots, children, independent } = useMemo(() => buildTree(teams), [teams]);

  const [collapsedTeamIds, setCollapsedTeamIds] = useState<Set<number>>(new Set());
  const collapsibleTeamIds = useMemo(() => new Set(children.keys()), [children]);
  const isAllCollapsed = useMemo(() => {
    if (collapsibleTeamIds.size === 0) {
      return false;
    }
    for (const id of collapsibleTeamIds) {
      if (!collapsedTeamIds.has(id)) {
        return false;
      }
    }
    return true;
  }, [collapsibleTeamIds, collapsedTeamIds]);

  const [floatingMenu, setFloatingMenu] = useState<{ team: Team; top: number; left: number } | null>(null);
  const [dropTargetId, setDropTargetId] = useState<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [editTeam, setEditTeam] = useState<Team | null>(null);
  const [createParentId, setCreateParentId] = useState<number | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteConfirmTeam, setDeleteConfirmTeam] = useState<Team | null>(null);
  const [manageTeamId, setManageTeamId] = useState<number | null>(null);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'horizontal' | 'tree'>('horizontal');
  const [compactView, setCompactView] = useState(false);

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const chartRef = React.useRef<HTMLDivElement | null>(null);
  const compactScrollAnchor = React.useRef<number | null>(null);
  const hasCenteredOnLoad = React.useRef(false);

  const updateHorizontalScrollState = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  const createSafeResizeObserver = (callback: () => void) => {
    if (typeof window === 'undefined' || typeof (window as any).ResizeObserver !== 'function') {
      return { observe: () => {}, disconnect: () => {} };
    }

    try {
      const observer = new (window as any).ResizeObserver(callback);
      if (typeof observer.observe !== 'function' || typeof observer.disconnect !== 'function') {
        return { observe: () => {}, disconnect: () => {} };
      }
      return observer;
    } catch {
      return { observe: () => {}, disconnect: () => {} };
    }
  };

  React.useEffect(() => {
    updateHorizontalScrollState();
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateHorizontalScrollState);
    window.addEventListener('resize', updateHorizontalScrollState);

    const chartEl = chartRef.current;
    const resizeObserver = createSafeResizeObserver(updateHorizontalScrollState);
    if (chartEl && typeof resizeObserver.observe === 'function') {
      resizeObserver.observe(chartEl);
    }

    return () => {
      el.removeEventListener('scroll', updateHorizontalScrollState);
      window.removeEventListener('resize', updateHorizontalScrollState);
      if (typeof resizeObserver.disconnect === 'function') {
        resizeObserver.disconnect();
      }
    };
  }, [updateHorizontalScrollState, teams.length, selectedTeamId, collapsedTeamIds]);

  const centerChart = useCallback(() => {
    const el = containerRef.current;
    const chartEl = chartRef.current;
    if (!el || !chartEl) return;

    const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
    if (maxScrollLeft <= 0) {
      hasCenteredOnLoad.current = true;
      return;
    }

    el.scrollTo({ left: Math.round(maxScrollLeft / 2), behavior: 'smooth' });
    updateHorizontalScrollState();
    hasCenteredOnLoad.current = true;
  }, [updateHorizontalScrollState]);

  const preserveCompactScrollPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    compactScrollAnchor.current = el.scrollLeft;
  }, []);

  const restoreCompactScrollPosition = useCallback(() => {
    const el = containerRef.current;
    const chartEl = chartRef.current;
    const anchor = compactScrollAnchor.current;
    if (!el || !chartEl || anchor == null) return;

    const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
    const boundedLeft = Math.min(Math.max(0, Math.round(anchor)), maxScrollLeft);
    el.scrollTo({ left: boundedLeft, behavior: 'auto' });
    compactScrollAnchor.current = null;
  }, []);

  React.useLayoutEffect(() => {
    if (compactScrollAnchor.current == null) return;
    let frameId = 0;
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      frameId = window.requestAnimationFrame(() => {
        frameId = window.requestAnimationFrame(restoreCompactScrollPosition);
      });
    } else {
      restoreCompactScrollPosition();
    }
    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [compactView, restoreCompactScrollPosition]);

  React.useEffect(() => {
    if (hasCenteredOnLoad.current) return;

    const el = containerRef.current;
    const chartEl = chartRef.current;
    if (!el || !chartEl) return;

    const tryCenter = () => {
      if (!el || !chartEl) return;
      const maxScrollLeft = Math.max(0, chartEl.scrollWidth - el.clientWidth);
      if (maxScrollLeft > 0) {
        centerChart();
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(tryCenter);
      });
    } else {
      tryCenter();
    }

    const resizeObserver = createSafeResizeObserver(() => {
      if (!hasCenteredOnLoad.current) {
        tryCenter();
      }
    });

    if (chartEl && typeof resizeObserver.observe === 'function') {
      resizeObserver.observe(chartEl);
    }

    return () => {
      if (typeof resizeObserver.disconnect === 'function') {
        resizeObserver.disconnect();
      }
    };
  }, [centerChart, teams.length, selectedTeamId]);

  const scrollHorizontally = (offset: number) => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollBy({ left: offset, behavior: 'smooth' });
  };

  const scrollLeft = () => scrollHorizontally(-420);
  const scrollRight = () => scrollHorizontally(420);

  React.useEffect(() => {
    if (!floatingMenu) return;

    const onDocDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('.org-node-menu') || el.closest('.org-node-menu-button')) return;
      setFloatingMenu(null);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFloatingMenu(null);
      }
    };

    const onScrollOrResize = () => {
      setFloatingMenu(null);
    };

    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [floatingMenu]);


  if (!teams?.length) {
    return <EmptyMessage>Create teams to build an organizational structure.</EmptyMessage>;
  }

  const handleEdit = (team: Team) => {
    setEditTeam(team);
    setIsEditOpen(true);
  };

  const handleFilterSelect = (team: Team | null) => {
    if (team) {
      setSelectedTeamId(String(team.id));
      onSelectedTeamChange?.(team);
    } else {
      setSelectedTeamId('');
      onSelectedTeamChange?.(null);
    }
  };

  const handleAddSub = (parentId: number) => {
    setCreateParentId(parentId);
    setIsCreateOpen(true);
  };

  const handleDeleteClick = (team: Team) => {
    setDeleteConfirmTeam(team);
  };

  const collectDescendants = (startId: number, childrenMap: Map<number, Team[]>): Set<number> => {
    const seen = new Set<number>();
    const stack = [startId];
    while (stack.length) {
      const id = stack.pop()!;
      const ch = childrenMap.get(id) || [];
      for (const c of ch) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          stack.push(c.id);
        }
      }
    }
    return seen;
  };

  const onDragStart = (e: React.DragEvent, team: Team) => {
    e.dataTransfer.setData('text/plain', String(team.id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOverBox = (e: React.DragEvent, team: Team) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(team.id);
  };

  const onDragLeaveBox = (_e: React.DragEvent, team: Team) => {
    setDropTargetId((current) => (current === team.id ? null : current));
  };

  const onToggleCollapse = (teamId: number) => {
    setCollapsedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  };

  const onDropOnBox = async (e: React.DragEvent, team: Team) => {
    e.preventDefault();
    setDropTargetId(null);
    const payload = e.dataTransfer.getData('text/plain');
    const draggedId = Number(payload);
    if (!draggedId || draggedId === team.id) return;

    const descendants = collectDescendants(draggedId, children);
    if (descendants.has(team.id)) {
      alert('Cannot move a team into one of its own descendants.');
      return;
    }

    try {
      await updateTeam({ id: draggedId, parent_team: team.id }).unwrap();
    } catch (err) {
      console.error('Failed to move team', err);
      alert('Failed to move team.');
    }
  };

  const onOpenMenu = (team: Team, target: HTMLElement) => {
    const MENU_H = 40;
    const targetRect = target.getBoundingClientRect();
    const wrapperEl = target.closest('.org-node-wrapper') as HTMLElement | null;
    const nodeBox = wrapperEl ? (wrapperEl.querySelector('.org-node-box') as HTMLElement | null) : null;

    let left: number;
    let top: number;

    if (nodeBox) {
      const boxRect = nodeBox.getBoundingClientRect();
      const btnCenter = targetRect.left + targetRect.width / 2;
      const preferredLeft = btnCenter - MENU_WIDTH / 2;
      const minLeft = Math.max(8, boxRect.left + 8);
      const maxLeft = Math.min(window.innerWidth - MENU_WIDTH - 8, boxRect.right - MENU_WIDTH - 8);
      const effectiveLeft = Math.min(maxLeft, Math.max(minLeft, preferredLeft));
      left = effectiveLeft;
      const VERTICAL_OFFSET = 6;
      top = Math.max(8, targetRect.bottom + VERTICAL_OFFSET);
      if (top + MENU_H > window.innerHeight - 8) {
        top = Math.max(8, targetRect.top - MENU_H - 8);
      }
    } else {
      const preferredLeft = targetRect.left + targetRect.width / 2 - MENU_WIDTH / 2;
      left = Math.max(8, Math.min(preferredLeft, window.innerWidth - MENU_WIDTH - 8));
      top = targetRect.bottom + 8;
      if (top + MENU_H > window.innerHeight - 8) {
        top = Math.max(8, targetRect.top - MENU_H - 8);
      }
    }

    setFloatingMenu({ team, top, left });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmTeam) return;
    const shouldArchive = Boolean(deleteConfirmTeam.has_snapshot_data);
    try {
      if (shouldArchive) {
        await archiveTeam(deleteConfirmTeam.id).unwrap();
      } else {
        await deleteTeam(deleteConfirmTeam.id).unwrap();
      }
      setDeleteConfirmTeam(null);
    } catch (error) {
      console.error(`Failed to ${shouldArchive ? 'archive' : 'delete'} team:`, error);
      alert(`Failed to ${shouldArchive ? 'archive' : 'delete'} team. Please try again.`);
    }
  };

  return (
    <OuterWrapper>
      <ChartHeader>
        <FilterGroup>
          <FilterSelect
            id="team-subtree-filter"
            value={selectedTeamId}
            onChange={(e) => {
              const nextValue = e.target.value;
              setSelectedTeamId(nextValue);
              const nextTeam = nextValue
                ? teams.find((team) => team.id === Number(nextValue)) || null
                : null;
              onSelectedTeamChange?.(nextTeam);
            }}
          >
            <option value="">All Teams</option>
            {allTeamsSorted.map((team) => (
              <option key={team.id} value={String(team.id)}>{team.name}</option>
            ))}
          </FilterSelect>
        </FilterGroup>
        <ScrollControls>
          <ScrollButton
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); centerChart(); }}
            disabled={layoutMode === 'tree'}
            aria-label="Center org chart"
            title={layoutMode === 'tree' ? 'Center disabled in tree view' : 'Center org chart'}
          >
            <Icon name="align center" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
          </ScrollButton>
          <ScrollButton type="button" onMouseDown={(e) => e.stopPropagation()} onClick={scrollLeft} disabled={!canScrollLeft} aria-label="Scroll left">
            <Icon name="angle left" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
          </ScrollButton>
          <ScrollButton type="button" onMouseDown={(e) => e.stopPropagation()} onClick={scrollRight} disabled={!canScrollRight} aria-label="Scroll right">
            <Icon name="angle right" style={{ fontSize: 16, lineHeight: 1, transform: 'translateX(2px)' }} />
          </ScrollButton>
        </ScrollControls>
        <LayoutToggleGroup>
          <LayoutToggleButton
            type="button"
            active={layoutMode === 'tree'}
            onClick={() => setLayoutMode((current) => (current === 'tree' ? 'horizontal' : 'tree'))}
            aria-label={layoutMode === 'tree' ? 'Org chart view' : 'Tree view'}
            title={layoutMode === 'tree' ? 'Org chart view' : 'Tree view'}
          >
            <Icon
              name={layoutMode === 'tree' ? 'sitemap' : 'list'}
              style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
            />
          </LayoutToggleButton>
          <LayoutToggleButton
            type="button"
            active={compactView}
            onClick={() => {
              preserveCompactScrollPosition();
              setCompactView((current) => !current);
            }}
            aria-label={compactView ? 'Expanded view' : 'Compact view'}
            title={compactView ? 'Expanded view' : 'Compact view'}
          >
            <Icon
              name={compactView ? 'expand' : 'compress'}
              style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
            />
          </LayoutToggleButton>
          <LayoutToggleButton
            type="button"
            active={isAllCollapsed}
            onClick={() => setCollapsedTeamIds((current) => (isAllCollapsed ? new Set() : new Set(collapsibleTeamIds)))}
            disabled={collapsibleTeamIds.size === 0}
            aria-label={isAllCollapsed ? 'Expand Chart' : 'Collapse Chart'}
            title={isAllCollapsed ? 'Expand Chart' : 'Collapse Chart'}
          >
            <Icon
              name={isAllCollapsed ? 'plus' : 'minus'}
              style={{ fontSize: 18, lineHeight: 1, transform: 'translateX(2px)' }}
            />
          </LayoutToggleButton>
        </LayoutToggleGroup>
      </ChartHeader>
      <Container
        ref={containerRef}
      >
        {selectedTeam ? (
          <Chart ref={chartRef} $layoutMode={layoutMode}>
            <Column key={selectedTeam.id}>
              <TeamOrgNode
                team={selectedTeam}
                childrenMap={children}
                onEdit={handleEdit}
                onManageClick={(t) => { setManageTeamId(t.id); setIsManageOpen(true); }}
                onFilterSelect={handleFilterSelect}
                selectedTeamId={selectedTeamId ? Number(selectedTeamId) : null}
                onOpenMenu={onOpenMenu}
                dropTargetId={dropTargetId}
                onDragStart={onDragStart}
                onDragOverBox={onDragOverBox}
                onDragLeaveBox={onDragLeaveBox}
                onDropOnBox={onDropOnBox}
                collapsedTeamIds={collapsedTeamIds}
                onToggleCollapse={onToggleCollapse}
                layoutMode={layoutMode}
                compactView={compactView}
              />
            </Column>
          </Chart>
        ) : roots.length > 0 ? (
        <Chart ref={chartRef} $layoutMode={layoutMode}>
          {roots.map((root) => (
            <Column key={root.id}>
              <TeamOrgNode
                team={root}
                childrenMap={children}
                onEdit={handleEdit}
                onManageClick={(t) => { setManageTeamId(t.id); setIsManageOpen(true); }}
                onFilterSelect={handleFilterSelect}
                selectedTeamId={selectedTeamId ? Number(selectedTeamId) : null}
                onOpenMenu={onOpenMenu}
                dropTargetId={dropTargetId}
                onDragStart={onDragStart}
                onDragOverBox={onDragOverBox}
                onDragLeaveBox={onDragLeaveBox}
                onDropOnBox={onDropOnBox}
                collapsedTeamIds={collapsedTeamIds}
                onToggleCollapse={onToggleCollapse}
                layoutMode={layoutMode}
                compactView={compactView}
              />
            </Column>
          ))}
        </Chart>
      ) : (
        <EmptyMessage>Assign parent teams to teams to create an organizational structure.</EmptyMessage>
      )}

      {!selectedTeam && independent.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <H2>Independent Teams</H2>
          <IndependentSection>
            {independent.map((team) => (
              <IndependentTeamCard
                key={team.id}
                team={team}
                dropTargetId={dropTargetId}
                onEdit={handleEdit}
                onOpenMenu={onOpenMenu}
                onFilterSelect={(team) => onSelectedTeamChange?.(team)}
                setManageTeamId={(teamId) => {
                  setManageTeamId(teamId);
                  setIsManageOpen(true);
                }}
                onDragStart={onDragStart}
                onDragOverBox={onDragOverBox}
                onDragLeaveBox={onDragLeaveBox}
                onDropOnBox={onDropOnBox}
              />
            ))}
          </IndependentSection>
        </div>
      )}

      {isCreateOpen && (
        <TeamEditModal
          isOpen={isCreateOpen}
          onClose={() => { setIsCreateOpen(false); setCreateParentId(null); }}
          onSuccess={() => { setIsCreateOpen(false); setCreateParentId(null); }}
          initialParentTeamId={createParentId}
        />
      )}

      {isEditOpen && editTeam && (
        <TeamEditModal
          isOpen={isEditOpen}
          onClose={() => { setIsEditOpen(false); setEditTeam(null); }}
          team={editTeam}
          onSuccess={() => { setIsEditOpen(false); setEditTeam(null); }}
        />
      )}

      {isManageOpen && manageTeamId && (
        <TeamMembersModal
          isOpen={isManageOpen}
          onClose={() => { setIsManageOpen(false); setManageTeamId(null); }}
          teamId={manageTeamId}
          onSuccess={() => {
            try { refetchAll?.(); } catch (e) { /* ignore */ }
            try { refetchMy?.(); } catch (e) { /* ignore */ }
            setIsManageOpen(false);
            setManageTeamId(null);
          }}
        />
      )}

      {floatingMenu && (
        <NodeMenu className="org-node-menu" style={{ position: 'fixed', top: floatingMenu.top, left: floatingMenu.left }}>
          <NodeMenuItem onClick={(e) => { e.stopPropagation(); setFloatingMenu(null); handleEdit(floatingMenu.team); }}>
            Edit
          </NodeMenuItem>
          <NodeMenuItem onClick={(e) => { e.stopPropagation(); setFloatingMenu(null); setManageTeamId(floatingMenu.team.id); setIsManageOpen(true); }}>
            Manage Members
          </NodeMenuItem>
          <NodeMenuItem onClick={(e) => { e.stopPropagation(); setFloatingMenu(null); handleAddSub(floatingMenu.team.id); }}>
            Create Sub-team
          </NodeMenuItem>
          <NodeMenuItem onClick={(e) => { e.stopPropagation(); setFloatingMenu(null); handleDeleteClick(floatingMenu.team); }} style={{ color: '#b91c1c' }}>
            {floatingMenu.team.has_snapshot_data ? 'Archive' : 'Delete'}
          </NodeMenuItem>
        </NodeMenu>
      )}

      {deleteConfirmTeam && (
        <PositionedModal open={!!deleteConfirmTeam} onClose={() => setDeleteConfirmTeam(null)} size="small">
          <div style={{ padding: 20 }}>
            <h3>{deleteConfirmTeam.has_snapshot_data ? 'Archive Team' : 'Delete Team'}</h3>
            {deleteConfirmTeam.has_snapshot_data ? (
              <p>
                Archive the team <strong>{deleteConfirmTeam.name}</strong>? Any reporting teams will become unassigned.
              </p>
            ) : (
              <p>Are you sure you want to delete <strong>{deleteConfirmTeam.name}</strong>? This action cannot be undone.</p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <StyledCancelButton onClick={() => setDeleteConfirmTeam(null)} disabled={isMutatingTeam} />
              {deleteConfirmTeam.has_snapshot_data ? (
                <StyledArchiveButton width="140px" onClick={confirmDelete} disabled={isMutatingTeam} label={isMutatingTeam ? 'Archiving...' : 'Archive Team'} />
              ) : (
                <StyledDeleteButton width="140px" onClick={confirmDelete} disabled={isMutatingTeam} label={isMutatingTeam ? 'Deleting...' : 'Delete Team'} />
              )}
            </div>
          </div>
        </PositionedModal>
      )}
    </Container>
  </OuterWrapper>
  );
};

export default TeamOrgStructure;
